import { motion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { Link } from "react-router-dom";

const MotionLink = motion(Link);

import mainImage from "../assets/about-1.webp";
import thumbnail1 from "../assets/about-2.webp";
import thumbnail2 from "../assets/about-3.webp";

interface AboutSectionProps {
  className?: string;
}

function AboutImages({
  mainImageY,
  thumbnailsY,
}: {
  mainImageY: any;
  thumbnailsY: any;
}) {
  return (
    <div className="w-full min-w-0 xl:min-w-[560px] xl:max-w-[1024px] xl:flex-[1.08_1_0] xl:self-stretch">
      <div
        className="
          grid w-full min-w-0 items-stretch
          grid-cols-1
          gap-4
          sm:grid-cols-[minmax(0,1fr)_minmax(190px,36%)]
          sm:gap-5
          lg:gap-6
          xl:h-full
        "
      >
        <motion.div
          style={{ y: mainImageY }}
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          whileHover={{ scale: 1.01 }}
          className="
            relative overflow-hidden rounded-2xl
            shadow-[0px_12px_42px_-4px_rgba(24,24,25,0.12),0px_8px_18px_-6px_rgba(24,24,25,0.12)]
            h-[clamp(320px,58vw,700px)]
            xl:h-full xl:min-h-0
          "
        >
          <img
            alt="Hamburguesas Stacy apiladas"
            className="absolute inset-0 h-full w-full object-cover"
            src={mainImage}
          />
        </motion.div>

        <motion.div
          style={{ y: thumbnailsY }}
          className="
            grid w-full min-w-0
            grid-cols-1
            gap-4
            sm:grid-rows-2
            sm:gap-5
            lg:gap-6
            xl:h-full
          "
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
            className="
              relative overflow-hidden rounded-2xl
              shadow-[0px_12px_42px_-4px_rgba(24,24,25,0.12),0px_8px_18px_-6px_rgba(24,24,25,0.12)]
              h-[clamp(180px,28vw,338px)]
              xl:h-full xl:min-h-0
            "
          >
            <img
              alt="Clientes en el mostrador de Stacy"
              className="absolute inset-0 h-full w-full object-cover"
              src={thumbnail1}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            whileHover={{ scale: 1.02 }}
            className="
              relative overflow-hidden rounded-2xl
              shadow-[0px_12px_42px_-4px_rgba(24,24,25,0.12),0px_8px_18px_-6px_rgba(24,24,25,0.12)]
              h-[clamp(180px,28vw,338px)]
              xl:h-full xl:min-h-0
            "
          >
            <img
              alt="Cliente disfrutando hamburguesa Stacy"
              className="absolute inset-0 h-full w-full object-cover"
              src={thumbnail2}
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

function AboutText({ textY }: { textY: any }) {
  return (
    <motion.div
      style={{ y: textY }}
      className="w-full min-w-0 xl:min-w-[420px] xl:max-w-[740px] xl:flex-[0.92_1_0]"
    >
      <div className="flex w-full min-w-0 flex-col items-start justify-center gap-4 text-[#181819] lg:gap-5">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="
            w-full min-w-0
            font-['Excessive_Extra_Condensed',sans-serif]
            leading-[0.9]
            not-italic
          "
        >
          <h2 className="m-0 font-['Excessive_Extra_Condensed'] text-[clamp(7rem,12vw,10.5rem)] leading-[0.9] tracking-[0.02em] uppercase">
            <span className="block">NO ES SOLO COMER.</span>
            <span className="block">ES STACY.</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="
            w-full min-w-0
            font-['Roboto_Mono',monospace]
            text-[16px] leading-[24px] tracking-[0.32px]
            lg:text-[18px] lg:leading-[30px]
            xl:text-[20px] xl:leading-[32px] xl:tracking-[0.4px]
          "
        >
          <p className="mb-0">
            <span>De pibes no necesitábamos mucho.</span>
            <span>{" "}La magia estaba en</span>
            <span className="font-bold"> animarse a jugar,</span>
            <span>
              {" "}en inventar reglas sobre la marcha, en olvidarse del tiempo y volver
              a casa con la sensación de que el día había valido la pena.
            </span>
            <span className="font-bold"> Eran cosas simples, pero se sentían gigantes.</span>
          </p>

          <p className="mb-0 mt-4 lg:mt-6">
            <span>
              Con el tiempo crecimos, llegaron las rutinas y eso que nos hacía vibrar
              quedó un poco atrás.
            </span>
            <span> Hasta que un día apareció una pregunta que cambió todo:</span>
            <span className="font-bold"> ¿y si volvemos a jugar?</span>
          </p>

          <p className="mb-0 mt-4 lg:mt-6">
            <span>De esa idea nació Stacy.</span>
            <span>
              {" "}Entre amigos, con ganas de <span className="font-bold">reunir al equipo,</span>
              {" "}recuperar esa energía y convertirla en algo real.
            </span>
            <span>
              {" "}Lo que empezó como <span className="font-bold">una excusa para volver a jugar</span>
              {" "}terminó convirtiéndose en una forma de hacer las cosas, de compartir
              y de crear algo con <span className="font-bold">identidad propia.</span>
            </span>
          </p>

          <MotionLink
            to="/about"
            onClick={() => window.scrollTo(0, 0)}
            whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
            whileTap={{ scale: 0.95 }}
            className="mt-4 flex min-h-[64px] max-w-[580px] w-full items-center justify-center gap-2.5 rounded-lg bg-[#BA4643] px-6 py-4 sm:w-auto sm:min-w-[240px] lg:mt-6"
          >
            <p className="font-['Roboto_Mono',monospace] text-lg font-medium tracking-[0.02em] text-[#F2F1DE] sm:text-xl">
              CONOCÉ NUESTRA HISTORIA
            </p>
          </MotionLink>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function AboutSection({ className = "" }: AboutSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
    layoutEffect: false,
  });

  const mainImageY = useTransform(scrollYProgress, [0, 1], [24, -24]);
  const thumbnailsY = useTransform(scrollYProgress, [0, 1], [16, -16]);
  const textY = useTransform(scrollYProgress, [0, 1], [12, -12]);

  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0.96, 1, 1, 0.98]);

  return (
    <section
      id="about"
      ref={sectionRef}
      className={`relative w-full bg-[#f2f1de] px-4 py-[60px] sm:px-6 sm:py-[70px] md:px-8 lg:px-10 lg:py-[80px] xl:px-16 ${className}`}
      data-name="About Section"
    >
      <motion.div
        style={{ opacity, scale }}
        className="mx-auto w-full max-w-[1920px]"
      >
        <div
          className="
            flex w-full flex-col gap-12
            lg:gap-14
            xl:grid xl:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] xl:items-stretch xl:gap-10
            2xl:gap-16
          "
        >
          <AboutImages
            mainImageY={mainImageY}
            thumbnailsY={thumbnailsY}
          />

          <AboutText textY={textY} />
        </div>
      </motion.div>
    </section>
  );
}